import { NONE_UNIFORM } from "../../../../../sports/common/players/NoPlayer";
import FootballState from "../../../../../sports/football/state/football-state";
import {
    getFieldDirectionMultiplier,
    getGoodFgKickedToSpot,
    getTouchbackYardline
} from "../../../../../sports/football/utils/context-utils";
import { FragmentSerializer } from "./FragmentSerializer";

export class FieldGoalAttemptFragmentSerializer extends FragmentSerializer {
    applicable(fragment) {
        return fragment.associatedState === FootballState.FieldGoal;
    }

    getRawFragment(play, serializationContext, fragment) {
        let { kickedBy, kickedFrom } =
            play.getFirstFragment(FootballState.Kick) || {};
        const fgMissed = play.getFirstFragment(FootballState.FieldGoalMissed);
        const fgOOB = play.getFirstFragment(FootballState.FieldGoalOutOfBounds);

        const isMissedTouchback =
            fgMissed &&
            play
                .getFragmentsBetween(fragment, [FootballState.CflKick])
                .find((f) => f.associatedState === FootballState.TouchBack);
        const fgBlocked = play.getFirstFragment(FootballState.FieldGoalBlocked);
        const fgBlockedRecovery = play
            .getFragmentsBetween(fragment, [FootballState.CflKick])
            .find((f) =>
                [
                    FootballState.KickNotRecovered,
                    FootballState.KickRecovered
                ].some((s) => s === f.associatedState)
            );
        const fgGood = play.getFirstFragment(FootballState.FieldGoalGood);
        const fgReturned = play
            .getFragmentsBetween(fragment, [FootballState.CflKick])
            .find((f) => f.associatedState === FootballState.Returned);
        const fgFumble = play.getFirstFragment(FootballState.FieldGoalFumble);

        const context = play.getContextSnapshot();
        const { homeTeamHasPossession, fieldPosition } = context;
        const { fieldLength } =
            serializationContext.gameContext.gameConfig.general;

        // TODO: Collect a 'missType' requiredData from FieldGoalMissed fragment
        // From Legacy - Wide Left(L), Wide Right(R), Short(S), Crossbar(C), Hit Left Up Right(1), and Hit Right up Right(2)
        const fgMissedType = "S";

        const hitGoalpost = play.getFirstFragment(FootballState.HitGoalpost);
        const barBallFragment =
            play.getFirstFragment(FootballState.BarBallRecoveredKicking) ||
            play.getFirstFragment(FootballState.BarBallRecoveredReceiving);

        let fgResultCode =
            fgBlocked?.code ||
            fgGood?.code ||
            fgReturned?.code ||
            fgMissed?.code ||
            fgFumble?.code ||
            fgOOB?.code;

        if (!fgResultCode && hitGoalpost) {
            fgResultCode = "M"; // Missed — ball hit the goalpost
        }

        if (!kickedFrom) {
            kickedFrom = fieldPosition;
        }

        const kickedTo =
            fgMissed?.kickedTo ||
            fgBlocked?.kickedTo ||
            fgFumble?.kickedTo ||
            fgOOB?.fieldPosition ||
            barBallFragment?.getFieldPosition() ||
            (homeTeamHasPossession ? fieldLength : 0);

        const touchbackPosition = getTouchbackYardline(
            false,
            !homeTeamHasPossession,
            serializationContext.gameContext.gameConfig
        );

        const kickedToLocation = fgGood
            ? getGoodFgKickedToSpot(context, kickedTo)
            : kickedTo;
        const distance =
            (kickedToLocation - kickedFrom) *
            getFieldDirectionMultiplier(context);

        const formattedKickedToPosition = this.resolveFormattedFieldPosition(
            serializationContext,
            kickedTo
        );

        const formattedContextFieldPosition =
            this.resolveFormattedFieldPosition(
                serializationContext,
                context.fieldPosition
            );

        const formattedKickedToFieldPosition =
            this.resolveFormattedFieldPosition(
                serializationContext,
                kickedToLocation
            );

        const formattedTbFieldPosition = this.resolveFormattedFieldPosition(
            serializationContext,
            touchbackPosition
        );

        let values = [
            this.resolveUniform(serializationContext, kickedBy),
            distance,
            fgResultCode
        ];

        if (fgReturned) {
            values = [...values, formattedKickedToPosition];
        } else if (fgMissed) {
            values = [
                ...values,
                isMissedTouchback
                    ? formattedTbFieldPosition
                    : formattedContextFieldPosition,
                fgMissedType
            ];
        } else if (fgOOB) {
            values = [...values, formattedKickedToFieldPosition, fgMissedType];
        } else if (fgBlocked) {
            const formattedRecoveredAtPosition =
                this.resolveFormattedFieldPosition(
                    serializationContext,
                    fgBlockedRecovery.recoveredAt
                );
            values = [
                ...values,
                formattedRecoveredAtPosition,
                this.resolveTeam(
                    serializationContext,
                    fgBlockedRecovery.recoveredBy
                ),
                this.resolveUniform(
                    serializationContext,
                    fgBlockedRecovery.recoveredBy
                ),
                ` BLOCK:${this.resolveUniform(
                    serializationContext,
                    fgBlocked.blockedBy
                )}`
            ];
        } else if (fgFumble) {
            values = [
                ...values,
                formattedKickedToPosition,
                this.resolveTeam(serializationContext, kickedBy),
                NONE_UNIFORM
            ];
        } else if (!fgGood && hitGoalpost) {
            values = [...values, formattedKickedToFieldPosition, "C"]; // C = crossbar
        }

        if (fragment.isDropKick) {
            values.push("DK");
        }

        const rawPlay = `FGA:${values.join()}`;
        return rawPlay;
    }
}
